import type { Organization, OTPLoginData, otpResponse, User } from '../interfaces/User';
import {
	JWT_SESSION_STORAGE,
	ORG_INFO_SESSION_STORAGE,
	ORG_SESSION_STORAGE,
	OTP_SESSION_STORAGE,
	USER_ID_SESSION_STORAGE,
	USER_INFO_SESSION_STORAGE,
} from '../shared/constants/constants';
import { initializePendo } from '../shared/helpers/pendoInitializer';
import axios from 'axios';

const createApiUri = (path: string = '') => '/api/v1/iam' + path;

export const otpLoginUri = createApiUri('/otp-login');

export const otpLogin = async (otp: string): Promise<OTPLoginData> => {
	const response = await axios.create().post(otpLoginUri, undefined, {
		headers: {
			Authorization: `Bearer ${otp}`,
		},
	});

	return {
		...response.data.data.userProfile,
		platformToken: response.headers['platform-token'],
	};
};

export const createFetchUserUri = (userId: string) => createApiUri('/users') + '/' + userId;

export const fetchUser = async (userId: string): Promise<User> => {
	const response = await axios.get(createFetchUserUri(userId));
	return response.data.data;
};

export const fetchOrgs = async (): Promise<Organization[]> => {
	const userOrgsPath = 'clients/orgs?root-orgs-only=false';
	const response = await axios.get(createFetchUserUri(userOrgsPath), { headers: { skipAppId: true } });
	return response.data.data;
};

export const logoutUserUri = createApiUri('/logout');

export const logoutUser = async (): Promise<void> => {
	await axios.post(logoutUserUri);
	sessionStorage.clear();
	window.dispatchEvent(new Event('jwt'));
};

export const renewToken = async (): Promise<void> => {
	const otpResponse = await axios.get<otpResponse>('/api/v1/iam/otp');
	const otp = otpResponse.data.data.id;
	const otpLoginData = await otpLogin(otp);
	const userOrgs = await fetchOrgs();

	if (otpLoginData.platformToken) {
		sessionStorage.setItem(OTP_SESSION_STORAGE, otp);
		sessionStorage.setItem(JWT_SESSION_STORAGE, otpLoginData.platformToken);
		sessionStorage.setItem(USER_ID_SESSION_STORAGE, otpLoginData.userId);
		const userInfo = await fetchUser(otpLoginData.userId);
		if (userInfo) {
			sessionStorage.setItem(USER_INFO_SESSION_STORAGE, JSON.stringify(userInfo));
		}
		window.dispatchEvent(new Event('jwt'));
	}
	if (userOrgs) {
		sessionStorage.setItem(ORG_INFO_SESSION_STORAGE, JSON.stringify(getOrgInfo(userOrgs)));
	}
	initializePendo();
};

const simplifiedUserOrg = (currentOrgInfo: Organization): Organization => {
	const userOrg: Organization = {
		id: currentOrgInfo.id,
		name: currentOrgInfo.name,
		rootOrg: currentOrgInfo.rootOrg,
		accessible: currentOrgInfo.accessible,
		clientId: currentOrgInfo.clientId,
	};
	return userOrg;
};

export const getOrgInfo = (userOrgs: Organization[]): Organization | void => {
	const currentOrgInfo = userOrgs.find(org => org.id === sessionStorage.getItem(ORG_SESSION_STORAGE));

	if (currentOrgInfo) {
		return simplifiedUserOrg(currentOrgInfo);
	}
};

export const fetchUserPermissions = async () => {
	const response = await axios.get('api/v1/iam/permissions');
	return response.data.data.items;
};
